'use client'

import React, { ReactNode, useRef } from 'react'
import { useEnvContext } from 'next-runtime-env'
import { LDContext, withLDProvider } from 'launchdarkly-react-client-sdk'
import isEqual from 'lodash/isEqual'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'
import { User } from '@dc-shared-web/hooks/api/user/types'
import { Spinner } from '@dc-shared-web/components/base'

import Header from '@/app/core/components/Header'
import { switzer } from '@/app/core/utils/fonts'
import { cn } from '@/dc-shared-web/src'

type WithLDProviderContentProps = {
  children: ReactNode
}

const getProviderContext = (user: User | undefined): LDContext => {
  if (!user) {
    return {
      kind: 'user',
      key: 'anonymous',
      email: 'anonymous',
      anonymous: true,
    }
  }

  if (user.id === -1) {
    // Random 16 digit number
    const random16DigitNumber = Math.floor(
      1000000000000000 + Math.random() * 9000000000000000
    )
    const anonymousId = random16DigitNumber.toString(36)

    return {
      kind: 'user',
      anonymous: true,
      key: anonymousId,
      email: 'anonymous',
    }
  }

  const teamFields = user.teams.reduce(
    (result, team) => {
      result.teams.push(team.name)
      result.teams_uuids.push(team.id)
      result.teams_salesforce.push(team.customer_id)
      return result
    },
    {
      teams: [] as string[],
      teams_uuids: [] as string[],
      teams_salesforce: [] as string[],
    }
  )

  return {
    kind: 'user',
    anonymous: false,
    key: user.email,
    email: user.email,
    ...teamFields,
  }
}

const createLDProviderComponent = (context: LDContext, clientSideID: string) =>
  withLDProvider({
    clientSideID,
    context,
    options: {},
  })((({ children }: { children: ReactNode }) => <>{children}</>) as React.FC)

const useCachedLDProviderComponent = (
  user: User | undefined,
  clientSideID: string
) => {
  const cachedContext = useRef(getProviderContext(user))
  const cachedComponent = useRef(
    createLDProviderComponent(cachedContext.current, clientSideID)
  )
  const newContext = getProviderContext(user)

  if (!isEqual(cachedContext.current, newContext)) {
    cachedContext.current = newContext
    cachedComponent.current = createLDProviderComponent(
      newContext,
      clientSideID
    )
  }

  return cachedComponent.current as React.ComponentType<{
    children: React.ReactNode
  }>
}

function WithLDProvider({ children }: WithLDProviderContentProps) {
  const { NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID } = useEnvContext()
  const { user, isAuthenticating } = useAuth()

  const LDProvider = useCachedLDProviderComponent(
    isAuthenticating ? undefined : user,
    NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID as string
  )

  if (isAuthenticating) {
    return (
      <html lang='en'>
        <body className={cn(switzer.className, switzer.variable)}>
          <Header />
          <div className='tw-flex tw-justify-center tw-items-center tw-h-screen-minus-header tw-w-full'>
            <Spinner className='tw-h-12 tw-w-12' />
          </div>
        </body>
      </html>
    )
  }
  return <LDProvider>{children}</LDProvider>
}

export default WithLDProvider
